import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import CustomButton from "../../kit/button";
import StackKit from "../../kit/stack";
import { toast } from "react-toastify";

interface FileUploadBoxProps {
  onFileSelect: (file: File) => void;
  fileExel?: any;
  setFileExel?: any;
}
/**
 * FileUploadBox
 *
 * کامپوننت برای آپلود فایل اکسل با قابلیت:
 * - کشیدن و رها کردن فایل (drag & drop)
 * - انتخاب فایل از طریق دکمه
 * - اعتبارسنجی فرمت فایل (.xls و .xlsx)
 * - نمایش نام فایل پس از بارگذاری
 *
 * واکنش‌گرا طراحی شده است:
 * - دکمه انتخاب فایل در موبایل تمام عرض است
 * - متن فایل با طول زیاد در موبایل می‌شکند تا overflow ایجاد نشود
 *
 * @param onFileSelect Callback که فایل انتخاب‌شده را به والد باز می‌گرداند
 * @param fileExel فایل فعلی که بارگذاری شده (اختیاری)
 * @param setFileExel تابع برای به‌روزرسانی فایل بارگذاری‌شده (اختیاری)
 *
 * @returns JSX.Element
 */

const FileUploadBox = ({ onFileSelect, fileExel, setFileExel }: FileUploadBoxProps) => {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const validateFile = (file: File) => {
    const validExtensions = [".xls", ".xlsx"];
    const fileName = file.name.toLowerCase();

    if (!validExtensions.some((ext) => fileName.endsWith(ext))) {
      toast.error("فقط فایل اکسل مجاز است!");
      return false;
    }
    return true;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && validateFile(file)) {
      setSelectedFile(file);
      onFileSelect(file);
    }
  };

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => !selectedFile && fileRef.current?.click()}
      sx={{
        border: "2px dashed #666666",
        borderRadius: "12px",
        p: 4,
        backgroundColor: "#2e2e2e",
        color: "#ccc",
        textAlign: "center",
        cursor: selectedFile ? "default" : "pointer",
        position: "relative",
        transition: "0.3s",
        minHeight: "180px",
        justifyContent: "center",
        display: "flex",
        flexDirection: "column",
        "&:hover": { backgroundColor: selectedFile ? "#2e2e2e" : "#383838" },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {!fileExel ? (
        <>
          <Typography variant="h6" sx={{ mb: 1, color: "#767676" }}>
            آپلود فایل اکسل
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "#767676" }}>
            فایل خود را بکشید و در این بخش رها کنید
          </Typography>

          {/* Responsive Button Container */}
          <StackKit sx={{ width: "100%" }} direction={{ xs: "column", sm: "row" }} justifyContent="flex-end" gap={1}>
            <CustomButton variant="contained" size="small" sx={{width: { xs: "100%", sm: "auto" },backgroundColor: "#555",color: "#fff",fontSize: "12px","&:hover": { backgroundColor: "#777" },}} 
              onClick={(e) => { e.stopPropagation(); fileRef.current?.click();}}>
              انتخاب فایل
            </CustomButton>
          </StackKit>
        </>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 2, color: "#4caf50" }}>
            فایل با موفقیت بارگذاری شد ✅
          </Typography>
          <Typography variant="body2" sx={{ color: "#ccc", wordBreak: "break-all" }}>
            {fileExel && fileExel.name}
          </Typography>
        </>
      )}

      <input type="file" accept=".xls,.xlsx" ref={fileRef} style={{ display: "none" }} onChange={handleFileChange}/>
    </Box>
  );
};

export default FileUploadBox;
